/**
 * @fileoverview Board Evaluation - 盤面評価ユーティリティ
 *
 * 責務:
 * - 盤面全体のマッチ検出
 * - 有効手（プレイ可能な交換）の判定
 * - 詰み検出のための評価関数
 */

import { getMatchingPieces } from './matches.js';

/**
 * 内部ヘルパー: 盤面マッチ走査（共通ロジック）
 * @private
 * @param {Board} board - Boardインスタンス
 * @param {boolean} earlyExit - trueなら最初のマッチで即座に終了
 * @returns {Array<Array<Piece>>} マッチ群の配列
 */
function _collectMatchesInternal(board, earlyExit = false) {
  const allMatches = [];

  for (let row = 0; row < board.rows; row++) {
    for (let col = 0; col < board.cols; col++) {
      const piece = board.getPiece(row, col);
      if (!piece || piece.specialType) continue;

      const matches = getMatchingPieces(board, piece);
      if (matches.length > 0) {
        allMatches.push(matches);
        if (earlyExit) return allMatches; // 早期終了
      }
    }
  }

  return allMatches;
}

/**
 * 盤面全体のマッチを収集
 * cascade.jsのStep 1と同じロジック
 *
 * @param {Board} board - Boardインスタンス
 * @returns {Array<Array<Piece>>} マッチ群の配列
 *
 * @example
 * const matches = collectAllMatches(board);
 * if (matches.length > 0) {
 *   // マッチが存在する
 * }
 */
export function collectAllMatches(board) {
  return _collectMatchesInternal(board, false);
}

/**
 * 盤面で最初に見つかったマッチのみを収集
 * @param {Board} board
 * @returns {Array<Array<Piece>>}
 */
export function collectFirstMatch(board) {
  return _collectMatchesInternal(board, true);
}

/**
 * 指定位置のスワップが有効手かチェック
 * @private
 */
function isValidSwap(board, row, col, newRow, newCol) {
  const piece1 = board.getPiece(row, col);
  const piece2 = board.getPiece(newRow, newCol);

  if (!piece1 || !piece2) return false;

  board.swapPieces(row, col, newRow, newCol);
  const matches1 = getMatchingPieces(board, piece1);
  const matches2 = getMatchingPieces(board, piece2);
  board.swapPieces(newRow, newCol, row, col);

  return matches1.length > 0 || matches2.length > 0;
}

/**
 * 指定方向へのスワップ試行ヘルパー
 * @private
 */
function trySwapInDirection(board, row, col, dRow, dCol) {
  const newRow = row + dRow;
  const newCol = col + dCol;

  if (!board.isValidPosition(newRow, newCol)) return false;
  return isValidSwap(board, row, col, newRow, newCol);
}

/**
 * 有効な手が存在するか判定
 * 全セルについて隣接4方向へのスワップを試行し、
 * マッチが成立するかチェック（早期終了あり）
 *
 * @param {Board} board - Boardインスタンス
 * @returns {boolean} 有効な手が1つでも存在すればtrue
 *
 * @example
 * if (!hasAvailableMove(board)) {
 *   // 詰み状態 → 自動シャッフル
 * }
 */
export function hasAvailableMove(board) {
  for (let row = 0; row < board.rows; row++) {
    for (let col = 0; col < board.cols; col++) {
      if (!board.getPiece(row, col)) continue;

      // 右方向チェック（早期終了）
      if (trySwapInDirection(board, row, col, 0, 1)) return true;

      // 下方向チェック（早期終了）
      if (trySwapInDirection(board, row, col, 1, 0)) return true;
    }
  }

  return false;
}
